<template>
  <h2>
    {{ t('extras') }}
    <span v-if="booking.bookedCar.id !== booking.allocations[0]?.car?.id"
      >({{
        t('ofBookedCar', {
          car: booking.bookedCar.vehicleType.make.name + ' ' + booking.bookedCar.vehicleType.model,
        })
      }})</span
    >
  </h2>
  <div class="mt-5">
    <div class="relative">
      <ExtrasForm
        :key="extrasFormKey"
        :price-calculation="booking.priceCalculation"
        :immutable="immutable"
        :is-loading="isLoadingPrice"
        :disabled="disabled || areAllExtrasReadOnly"
        :disabled-extra-types="frozenExtraTypes"
        :show-title="false"
        @update:booked-extras="pendingBookedExtras = $event"
        @update:custom-booked-extras="pendingCustomBookedExtras = $event"
        @update:is-valid="isExtrasFormValid = $event"
      />

      <p v-if="hasChanged && !isLoadingPrice && newPricing" class="mt-2 font-medium">
        {{ t('newPrice') }}:
        <span v-currency="newPricing?.totalPrice"></span>
      </p>

      <p v-if="hasChanged && isLoadingPrice" class="mt-2 font-medium">
        {{ t('recalculatingPrice') }}
      </p>

      <div v-if="hasChanged" class="col-span-3 mt-2 flex">
        <CVButton
          variant="default"
          :disabled="isLoadingPrice || !isExtrasFormValid"
          :is-loading="isBusy"
          class="mr-2"
          @click.prevent="saveExtrasChange"
        >
          {{ t('save') }}
        </CVButton>
        <CVButton
          variant="secondary"
          :disabled="isLoadingPrice"
          class="mr-2"
          @click.prevent="cancelExtrasChange"
        >
          {{ t('cancel') }}
        </CVButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { getBookedExtras, getCustomBookedExtras } from '@/utils/pricing.utils';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { watchDebounced } from '@vueuse/core';
import { type Booking, type UpdateBookingDto } from '@/entities/bookings/booking.entity';
import CVButton from '@/components/buttons/CVButton.vue';
import { useUpdateBooking } from '@/queries/use-bookings.ts';
import {
  PriceRecalculationMode,
  type UpdateCustomBookedExtraDto,
  VehicleExtraType,
  CounterpartyType,
} from '@carvia/ros-client-types';
import type { Pricing } from '@/entities/pricing/pricing.entity';
import { areAllExtrasFrozen, useIsExtraTypeFrozen } from './use-is-extra-type-frozen';
import ExtrasForm, { type BookedExtraSelection } from '@/components/extras/ExtrasForm.vue';
import { usePriceChangeConfirmation } from '@/components/price-change-confirmation/price-change-confirmation';

const props = defineProps<{
  booking: Booking;
  disabled?: boolean;
  immutable?: boolean;
  priceDifferenceCounterparty?: CounterpartyType;
  handoverAdjustment?: boolean;
}>();

const emit = defineEmits<{
  (e: 'hasChanged', value: boolean): void;
}>();

const { t } = useI18n();

const newPricing = ref<Pricing | null>(null);
const { isPending: isUpdatingPrice, mutateAsync: updateBooking } = useUpdateBooking();
const { confirmAndSave, isBusy, reset } = usePriceChangeConfirmation();

const isLoadingPrice = computed(() => isBusy.value || isUpdatingPrice.value);

const pendingBookedExtras = ref<BookedExtraSelection[]>([]);
const pendingCustomBookedExtras = ref<UpdateCustomBookedExtraDto[] | undefined>(undefined);
const isExtrasFormValid = ref(true);

const areAllExtrasReadOnly = computed(() => areAllExtrasFrozen(props.booking.status));
const isExtraTypeFrozen = useIsExtraTypeFrozen(
  props.booking.status,
  props.handoverAdjustment,
  getCustomBookedExtras(props.booking.priceCalculation),
);
const frozenExtraTypes = computed(() => {
  return Object.values(VehicleExtraType).filter((type) => isExtraTypeFrozen(type));
});

const hasChanged = ref(false);

const extrasFormKey = ref(0);

watch(
  () => [
    getBookedExtras(props.booking.priceCalculation),
    getCustomBookedExtras(props.booking.priceCalculation),
  ],
  () => {
    extrasFormKey.value++;
    hasChanged.value = false;
    newPricing.value = null;
    pendingBookedExtras.value = [];
    pendingCustomBookedExtras.value = undefined;
  },
  { deep: true },
);

watchDebounced(
  [pendingBookedExtras, pendingCustomBookedExtras],
  async () => {
    hasChanged.value =
      !!pendingBookedExtras.value.length || !!pendingCustomBookedExtras.value?.length;
    if (!hasChanged.value) {
      newPricing.value = null;
      return;
    }
    const result = await updateBooking({
      id: props.booking.id,
      booking: {
        pricing: {
          priceRecalculationMode: PriceRecalculationMode.USE_EXISTING,
          bookedExtras: pendingBookedExtras.value,
          customBookedExtras: pendingCustomBookedExtras.value,
        },
        dryRun: true,
      },
      ...(props.handoverAdjustment ? { query: { handoverAdjustment: true as const } } : {}),
    });
    newPricing.value = result?.priceCalculation ?? null;
  },
  { debounce: 50, deep: true },
);

const cancelExtrasChange = () => {
  extrasFormKey.value++;
  pendingBookedExtras.value = [];
  pendingCustomBookedExtras.value = undefined;
  hasChanged.value = false;
  newPricing.value = null;
  reset();
};

const saveExtrasChange = async () => {
  if (!newPricing.value) {
    return;
  }
  const updateBookingDto: UpdateBookingDto = {
    pricing: {
      bookedExtras: pendingBookedExtras.value,
      customBookedExtras: pendingCustomBookedExtras.value,
      priceRecalculationMode: PriceRecalculationMode.USE_EXISTING,
    },
  };
  const { status } = await confirmAndSave({
    booking: props.booking,
    updateBookingDto,
    priceDifferenceCounterparty: props.priceDifferenceCounterparty,
    handoverAdjustment: props.handoverAdjustment,
    confirmOnlyWhenCancellationFee: true,
  });
  if (status === 'cancelled') {
    cancelExtrasChange();
    return;
  }
  if (status !== 'saved') {
    return;
  }
  extrasFormKey.value++;
  hasChanged.value = false;
  pendingCustomBookedExtras.value = undefined;
  pendingBookedExtras.value = [];
};

watch(hasChanged, (newValue) => emit('hasChanged', newValue));
</script>

<i18n lang="json">
{
  "en": {
    "extras": "Extras",
    "newPrice": "New Price",
    "recalculatingPrice": "Recalculating Price...",
    "ofBookedCar": "of booked {car}"
  },
  "de": {
    "extras": "Extras",
    "newPrice": "Neuer Preis",
    "recalculatingPrice": "Preis wird neu berechnet...",
    "ofBookedCar": "von gebuchtem {car}"
  },
  "es": {
    "extras": "Extras",
    "newPrice": "Nuevo precio",
    "recalculatingPrice": "Recalculando el precio…",
    "ofBookedCar": "del {car} reservado"
  }
}
</i18n>
